import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  Icon,
  useTheme,
  alpha,
  styled,
  Snackbar,
  Alert,
} from "@mui/material";

import { SessionResponse } from "../../../types/pos.types";
import { DenominationCount } from "./DenominationCount";
import { CashReconciliation } from "./CashReconciliation";
import { SessionSummary } from "./SessionSummary";

interface SessionCloseProps {
  open: boolean;
  onClose: () => void;
  sessionData: SessionResponse;
  onSubmit: (data: any) => void;
}

const steps = ["Count Denominations", "Cash Reconciliation", "Session Summary"];

// Custom colors to match the system theme
const THEME_COLORS = {
  primary: "#1B7B6B", // Deep teal from header
  primaryLight: "#1E8078", // Lighter teal for hover states
  primaryDark: "#166F68", // Darker teal for active states
  success: "#059669",
  background: "#FFFFFF",
  text: {
    primary: "#2C3E50",
    secondary: "#546E7A",
  },
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius * 2,
    backgroundImage: "none",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    width: "95%",
    maxWidth: 1400,
    margin: theme.spacing(2),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: THEME_COLORS.primary,
  color: THEME_COLORS.background,
  padding: theme.spacing(3),
  "& .MuiTypography-root": {
    color: THEME_COLORS.background,
  },
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: alpha(THEME_COLORS.primary, 0.04),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  "& .MuiStepLabel-root .Mui-active": {
    color: THEME_COLORS.primary,
  },
  "& .MuiStepLabel-root .Mui-completed": {
    color: THEME_COLORS.success,
  },
  "& .MuiStepConnector-line": {
    borderColor: alpha(THEME_COLORS.primary, 0.2),
  },
  "& .MuiStepIcon-root": {
    color: alpha(THEME_COLORS.primary, 0.2),
    "&.Mui-active": {
      color: THEME_COLORS.primary,
    },
    "&.Mui-completed": {
      color: THEME_COLORS.primary,
    },
  },
}));

const StyledStepContent = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: THEME_COLORS.primary,
    boxShadow: `0 4px 12px ${alpha(THEME_COLORS.primary, 0.08)}`,
  },
}));

const StyledActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  textTransform: "none",
  fontWeight: 500,
  transition: "all 0.2s ease-in-out",
  "&.MuiButton-contained": {
    backgroundColor: THEME_COLORS.primary,
    color: THEME_COLORS.background,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: THEME_COLORS.primaryLight,
      boxShadow: `0 4px 12px ${alpha(THEME_COLORS.primary, 0.2)}`,
    },
    "&:active": {
      backgroundColor: THEME_COLORS.primaryDark,
    },
  },
  "&.MuiButton-outlined": {
    borderColor: alpha(THEME_COLORS.primary, 0.5),
    color: THEME_COLORS.primary,
    "&:hover": {
      borderColor: THEME_COLORS.primary,
      backgroundColor: alpha(THEME_COLORS.primary, 0.04),
    },
  },
}));

export const SessionClose: React.FC<SessionCloseProps> = ({
  open,
  onClose,
  sessionData,
  onSubmit,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [denominationData, setDenominationData] = useState<{
    bills: Array<{ value: number; count: number }>;
    coins: Array<{ value: number; count: number }>;
  }>({
    bills: [],
    coins: [],
  });

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      onSubmit({
        session_id: sessionData.id,
        final_denominations: denominationData,
      });
      setShowSuccessAlert(true);
      // Close the dialog and navigate after 2 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
        handleCloseDialog();
        navigate("/pos/orders");
      }, 2000);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleDenominationSubmit = (data: {
    bills: Array<{ value: number; count: number }>;
    coins: Array<{ value: number; count: number }>;
  }) => {
    setDenominationData(data);
  };

  const handleCloseDialog = () => {
    setActiveStep(0);
    setDenominationData({
      bills: [],
      coins: [],
    });
    setShowSuccessAlert(false);
    onClose();
  };
  const renderStepContent = (step: number) => {
    if (!sessionData?.denominations?.initial) {
      return null;
    }

    switch (step) {
      case 0:
        return (
          <DenominationCount
            initialData={sessionData.denominations.initial}
            onSubmit={handleDenominationSubmit}
          />
        );
      case 1:
        return (
          <CashReconciliation
            denominationData={denominationData}
            sessionData={sessionData}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <SessionSummary
            sessionData={sessionData}
            finalDenominations={denominationData}
            onClose={() => {
              onSubmit({
                session_id: sessionData.id,
                final_denominations: denominationData,
              });
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          style: { minHeight: "60vh" },
        }}
      >
        <StyledDialogTitle>
          <Box>
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 600,
                mb: 1,
              }}
            >
              Close Session
            </Typography>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
              }}
            >
              Please follow the steps below to close your current session
            </Typography>
          </Box>
        </StyledDialogTitle>
        <DialogContent
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              backgroundColor: alpha(THEME_COLORS.primary, 0.02),
              borderRadius: 1,
              border: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${alpha(THEME_COLORS.primary, 0.1)}`,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                  color: THEME_COLORS.primary,
                  fontWeight: 500,
                }}
              >
                <Icon sx={{ mr: 1 }}>assignment</Icon>
                Session Close Steps
              </Typography>
              <StyledStepper activeStep={activeStep}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: THEME_COLORS.text.primary,
                        }}
                      >
                        {label}
                      </Typography>
                    </StepLabel>
                  </Step>
                ))}
              </StyledStepper>
            </Box>

            <Box sx={{ p: 2 }}>
              <StyledStepContent elevation={0}>
                {renderStepContent(activeStep)}
              </StyledStepContent>
            </Box>
          </Paper>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <StyledActionButton
            startIcon={<Icon>close</Icon>}
            onClick={handleCloseDialog}
            variant="outlined"
          >
            Cancel
          </StyledActionButton>
          <Box sx={{ flex: 1 }} />
          {activeStep > 0 && (
            <StyledActionButton
              startIcon={<Icon>arrow_back</Icon>}
              onClick={handleBack}
              variant="outlined"
            >
              Back
            </StyledActionButton>
          )}
          <StyledActionButton
            startIcon={
              activeStep === steps.length - 1 ? (
                <Icon>done</Icon>
              ) : (
                <Icon>arrow_forward</Icon>
              )
            }
            variant="contained"
            onClick={handleNext}
            size="large"
          >
            {activeStep === steps.length - 1 ? "Complete Session" : "Next"}
          </StyledActionButton>
        </DialogActions>
      </StyledDialog>

      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: THEME_COLORS.success,
            color: THEME_COLORS.background,
            "& .MuiAlert-icon": {
              color: THEME_COLORS.background,
            },
            boxShadow: `0 4px 12px ${alpha(THEME_COLORS.success, 0.2)}`,
          }}
        >
          Session closed successfully!
        </Alert>
      </Snackbar>
    </>
  );
};
